/**
 * @fileoverview Hook UI Layer para gestión de estado visual y handlers de ChatComponentMC.
 * @module useChatComponentUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook especializado para manejar el estado visual y handlers de UI del ChatComponentMC.
 *
 * - Estado de menú de adjuntos
 * - Estado de modal de orden
 * - Handlers para abrir/cerrar ambos
 * - Estado general de UI (sin lógica de input)
 *
 * @returns {Object} Estado y handlers UI
 * @property {boolean} openAttachMenu - Si el menú de adjuntos está abierto
 * @property {function} setOpenAttachMenu - Setter para menú de adjuntos
 * @property {function} handleAttachMenuClick - Handler para abrir menú de adjuntos
 * @property {boolean} showEmojiPicker - Si el selector de emojis está visible
 * @property {function} closeEmojiPicker - Cierra el selector de emojis
 * @property {function} toggleEmojiPicker - Alterna visibilidad del selector de emojis
 * @property {Object} textareaRef - Ref al textarea del chat
 * @property {Object|null} selectedFastAnswer - Respuesta rápida seleccionada
 * @property {function} setSelectedFastAnswer - Setter para respuesta rápida seleccionada
 * @property {Object|null} selectedProduct - Producto seleccionado
 * @property {function} setSelectedProduct - Setter para producto seleccionado
 */
function useChatComponentUI() {
  const textareaRef = useRef(null);

  const [openAttachMenu, setOpenAttachMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFastAnswer, setSelectedFastAnswer] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleAttachMenuClick = useCallback(() => {
    setOpenAttachMenu(true);
  }, []);

  const closeEmojiPicker = useCallback(() => {
    setShowEmojiPicker(false);
  }, []);

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') closeEmojiPicker();
    };

    if (showEmojiPicker) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showEmojiPicker, closeEmojiPicker]);

  return {
    openAttachMenu,
    setOpenAttachMenu,
    handleAttachMenuClick,
    showEmojiPicker,
    closeEmojiPicker,
    toggleEmojiPicker,
    textareaRef,
    selectedFastAnswer,
    setSelectedFastAnswer,
    selectedProduct,
    setSelectedProduct,
  };
}

export default useChatComponentUI;
